export function getImbalanceSuggestion(
  ratio: number,
  numClasses: number,
  totalRows: number,
  minCount: number,
  _maxCount: number,
  totalNonNull: number
): string[] {
  const suggestions: string[] = [];
  const isBinary = numClasses === 2;
  // Use totalNonNull (sum of all class counts) for accurate minority %
  const minorityPct = totalNonNull > 0 ? ( minCount / totalNonNull * 100 ).toFixed( 1 ) : '0.0';

  // Base assessment
  if ( ratio < 1.5 ) {
    suggestions.push( `Well-balanced dataset (${ minorityPct }% minority class). No rebalancing needed.` );
  } else if ( ratio < 2 ) {
    suggestions.push( `Near-balanced (${ minorityPct }% minority). Most models will handle this without intervention.` );
  } else if ( ratio < 5 ) {
    suggestions.push( `Moderate imbalance (${ minorityPct }% minority, ${ ratio.toFixed( 1 ) }:1 ratio).` );
  } else if ( ratio < 10 ) {
    suggestions.push( `Significant imbalance (${ minorityPct }% minority, ${ ratio.toFixed( 1 ) }:1 ratio).` );
  } else if ( ratio < 50 ) {
    suggestions.push( `Severe imbalance (${ minorityPct }% minority, ${ ratio.toFixed( 1 ) }:1 ratio).` );
  } else {
    suggestions.push( `Extreme imbalance (${ minorityPct }% minority, ${ ratio.toFixed( 0 ) }:1 ratio). Consider framing as anomaly detection.` );
  }

  // Data-driven technique recommendations
  if ( ratio >= 2 ) {
    // Class weights — always applicable for imbalanced data
    suggestions.push( 'Use class_weight=\'balanced\' (sklearn) or weighted loss functions to penalize minority misclassification.' );

    // Size-aware resampling advice
    if ( ratio >= 5 && minCount < 500 && totalRows < 5000 ) {
      suggestions.push( `Minority class has only ${ minCount } samples. SMOTE may generate noisy synthetic examples — consider collecting more data or using data augmentation.` );
    } else if ( ratio >= 5 && minCount >= 500 ) {
      suggestions.push( `SMOTE or ADASYN can generate synthetic minority samples. Minority class (${ minCount } rows) has enough real examples for quality synthesis.` );
    }

    if ( ratio >= 3 && totalRows > 10000 ) {
      suggestions.push( `Dataset is large enough (${ totalRows.toLocaleString() } rows) for random undersampling of the majority class without significant information loss.` );
    }

    if ( ratio >= 10 ) {
      suggestions.push( `At ${ ratio.toFixed( 0 ) }:1 ratio, standard metrics (accuracy) are misleading. Use precision, recall, F1, and AUC-PR as primary evaluation metrics.` );
    }
  }

  // Multi-class specific advice
  if ( !isBinary && numClasses > 2 ) {
    suggestions.push( `Multi-class (${ numClasses } classes): use stratified k-fold cross-validation to preserve class proportions in each fold.` );
    if ( ratio >= 5 ) {
      suggestions.push( 'Consider one-vs-rest decomposition with per-class rebalancing for highly imbalanced multi-class problems.' );
    }
  }

  // Stratified split reminder
  if ( ratio >= 2 ) {
    suggestions.push( 'Always use stratified train/test splits to maintain class proportions in both sets.' );
  }

  return suggestions;
}

export function getMissingValueAdvice( _col: string, nullPct: number, isNumeric: boolean, totalRows: number, nullCount: number ): string {
  if ( nullPct === 0 ) return '';

  // Common suffix: missingness itself can be a signal
  const missingnessNote = nullPct >= 20
    ? ' Note: missingness may be informative (e.g., no feedback = no complaint = satisfied customer). Always create an "is_missing" binary feature before dropping.'
    : '';

  if ( nullPct < 1 ) {
    return nullCount <= 10
      ? `${ nullCount } missing — safe to drop these rows`
      : `< 1% missing (${ nullCount } rows) — drop rows or impute with ${ isNumeric ? 'median' : 'mode' }`;
  }
  if ( nullPct < 5 ) {
    return isNumeric
      ? 'Impute with median (robust to outliers) or mean. Check if missingness correlates with the target.'
      : 'Impute with mode or create an "Unknown" category. Check if missingness correlates with the target.';
  }
  if ( nullPct < 20 ) {
    return isNumeric
      ? 'Consider multiple imputation (MICE) or median imputation. Check if missingness correlates with target variable — it may be a useful signal.'
      : 'Create "Missing" category or use model-based imputation. Investigate if missingness is informative (absence of data can indicate a pattern).';
  }
  if ( nullPct < 50 ) {
    return `High missingness (${ nullCount.toLocaleString() } of ${ totalRows.toLocaleString() } rows). Create a binary "is_missing" indicator feature — the absence pattern may carry predictive signal. Impute remaining values with ${ isNumeric ? 'median' : 'mode' } if keeping the column.${ missingnessNote }`;
  }
  return `Majority missing (${ nullCount.toLocaleString() } of ${ totalRows.toLocaleString() } rows). The "is_missing" indicator is likely more predictive than the values themselves — absence of data often signals something (e.g., no feedback = no issue, no support ticket = satisfied).${ missingnessNote }`;
}
